import React, { useState, useEffect } from 'react';
import {
  CheckCircleIcon,
  ExclamationCircleIcon,
  ClockIcon,
  ArrowPathIcon,
  ChartBarIcon,
  EyeIcon,
} from '@heroicons/react/24/outline';
import { useCompany } from '../../contexts/CompanyContext';

// Tipos
interface SyncStatus {
  platform: string;
  platform_name: string;
  total_products: number;
  synced_products: number;
  failed_products: number;
  pending_products: number;
  last_sync: string | null;
  is_syncing: boolean;
  success_rate: number;
}

interface SyncLog {
  id: string;
  platform: string;
  action: string;
  status: 'success' | 'error' | 'pending' | 'processing';
  product_count: number;
  message: string;
  created_at: string;
  details?: any;
}

interface PlatformMetrics {
  platform: string;
  platform_name: string;
  total_exports: number;
  successful_exports: number;
  failed_exports: number;
  avg_processing_time: number;
}

const SyncDashboard: React.FC = () => {
  const { currentCompany } = useCompany();
  const [syncStatuses, setSyncStatuses] = useState<SyncStatus[]>([]);
  const [syncLogs, setSyncLogs] = useState<SyncLog[]>([]);
  const [platformMetrics, setPlatformMetrics] = useState<PlatformMetrics[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [selectedLog, setSelectedLog] = useState<SyncLog | null>(null);

  // Carregar dados do dashboard
  const loadDashboardData = async () => {
    if (!currentCompany) return;

    try {
      setIsLoading(true);

      // Carregar status de sincronização
      const statusResponse = await fetch(`/api/v1/sync/status/?company_id=${currentCompany.id}`, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('access_token')}`,
        },
      });

      // Carregar logs de sincronização
      const logsResponse = await fetch(`/api/v1/sync/logs/?company_id=${currentCompany.id}&limit=20`, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('access_token')}`,
        },
      });

      // Carregar métricas por plataforma
      const metricsResponse = await fetch(`/api/v1/sync/metrics/?company_id=${currentCompany.id}`, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('access_token')}`,
        },
      });

      if (statusResponse.ok) {
        const statusData = await statusResponse.json();
        setSyncStatuses(statusData.success ? statusData.data : []);
      }

      if (logsResponse.ok) {
        const logsData = await logsResponse.json();
        setSyncLogs(logsData.success ? logsData.data : []);
      }

      if (metricsResponse.ok) {
        const metricsData = await metricsResponse.json();
        setPlatformMetrics(metricsData.success ? metricsData.data : []);
      }
    } catch (error) {
      console.error('Erro ao carregar dashboard de sync:', error);
    } finally {
      setIsLoading(false);
    }
  };

  // Retry de falhas
  const handleRetryFailures = async (platform: string) => {
    try {
      const response = await fetch(`/api/v1/sync/retry-failures/`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('access_token')}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          company_id: currentCompany?.id,
          platform,
        }),
      });

      if (response.ok) {
        // Recarregar dados
        loadDashboardData();
      }
    } catch (error) {
      console.error('Erro ao tentar novamente as falhas:', error);
    }
  };

  // Auto-refresh a cada 30 segundos
  useEffect(() => {
    loadDashboardData();
    const interval = setInterval(loadDashboardData, 30000);
    return () => clearInterval(interval);
  }, [currentCompany]);

  const formatDate = (dateString: string) => {
    return new Intl.DateTimeFormat('pt-BR', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    }).format(new Date(dateString));
  };

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'success':
        return <CheckCircleIcon className="h-5 w-5 text-green-500" />;
      case 'error':
        return <ExclamationCircleIcon className="h-5 w-5 text-red-500" />;
      case 'processing':
        return <ArrowPathIcon className="h-5 w-5 text-blue-500 animate-spin" />;
      default:
        return <ClockIcon className="h-5 w-5 text-yellow-500" />;
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'success':
        return 'bg-green-100 text-green-800';
      case 'error':
        return 'bg-red-100 text-red-800';
      case 'processing':
        return 'bg-blue-100 text-blue-800';
      default:
        return 'bg-yellow-100 text-yellow-800';
    }
  };

  if (!currentCompany) {
    return (
      <div className="text-center py-8">
        <p className="text-gray-500">Selecione uma empresa para ver o dashboard de sincronização</p>
      </div>
    );
  }

  if (isLoading) {
    return (
      <div className="text-center py-8">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto"></div>
        <p className="mt-2 text-gray-500">Carregando dashboard...</p>
      </div>
    );
  }

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
      <div className="mb-8">
        <h1 className="text-2xl font-bold text-gray-900">
          Dashboard de Sincronização - {currentCompany.name}
        </h1>
        <p className="mt-2 text-gray-600">
          Monitore o status das sincronizações e exportações para as plataformas.
        </p>
      </div>

      {/* Status Cards por Plataforma */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 mb-8">
        {syncStatuses.map((status) => (
          <div key={status.platform} className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold text-gray-900">{status.platform_name}</h3>
              {status.is_syncing && (
                <ArrowPathIcon className="h-5 w-5 text-blue-500 animate-spin" />
              )}
            </div>

            <div className="grid grid-cols-2 gap-4 mb-4">
              <div>
                <div className="text-2xl font-bold text-green-600">{status.synced_products}</div>
                <div className="text-xs text-gray-500">Sincronizados</div>
              </div>
              <div>
                <div className="text-2xl font-bold text-red-600">{status.failed_products}</div>
                <div className="text-xs text-gray-500">Falhas</div>
              </div>
              <div>
                <div className="text-2xl font-bold text-yellow-600">{status.pending_products}</div>
                <div className="text-xs text-gray-500">Pendentes</div>
              </div>
              <div>
                <div className="text-2xl font-bold text-gray-900">{status.total_products}</div>
                <div className="text-xs text-gray-500">Total</div>
              </div>
            </div>

            {/* Progress Bar */}
            <div className="mb-3">
              <div className="flex justify-between text-xs text-gray-500 mb-1">
                <span>Taxa de Sucesso</span>
                <span>{status.success_rate.toFixed(1)}%</span>
              </div>
              <div className="w-full bg-gray-200 rounded-full h-2">
                <div
                  className="bg-green-600 h-2 rounded-full transition-all"
                  style={{ width: `${status.success_rate}%` }}
                ></div>
              </div>
            </div>

            <div className="flex items-center justify-between">
              <div className="text-xs text-gray-500">
                Última sync: {status.last_sync ? formatDate(status.last_sync) : 'Nunca'}
              </div>
              {status.failed_products > 0 && (
                <button
                  onClick={() => handleRetryFailures(status.platform)}
                  className="text-xs text-blue-600 hover:text-blue-800 font-medium"
                >
                  Tentar Novamente
                </button>
              )}
            </div>
          </div>
        ))}
      </div>

      {/* Métricas Agregadas */}
      {platformMetrics.length > 0 && (
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6 mb-8">
          <div className="flex items-center mb-4">
            <ChartBarIcon className="h-6 w-6 text-gray-600 mr-2" />
            <h3 className="text-lg font-semibold text-gray-900">Métricas de Performance</h3>
          </div>
          
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Plataforma
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Total Exports
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Sucessos
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Falhas
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Tempo Médio
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {platformMetrics.map((metric) => (
                  <tr key={metric.platform}>
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                      {metric.platform_name}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                      {metric.total_exports}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-green-600">
                      {metric.successful_exports}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-red-600">
                      {metric.failed_exports}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                      {metric.avg_processing_time.toFixed(1)}s
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Logs Recentes */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200">
        <div className="px-6 py-4 border-b border-gray-200">
          <h3 className="text-lg font-semibold text-gray-900">Logs Recentes</h3>
        </div>
        
        <div className="divide-y divide-gray-200">
          {syncLogs.length === 0 ? (
            <div className="p-6 text-center text-gray-500">
              Nenhum log encontrado
            </div>
          ) : (
            syncLogs.map((log) => (
              <div key={log.id} className="p-6 hover:bg-gray-50">
                <div className="flex items-center justify-between">
                  <div className="flex items-center space-x-3">
                    {getStatusIcon(log.status)}
                    <div>
                      <div className="flex items-center space-x-2">
                        <span className="font-medium text-gray-900">{log.action}</span>
                        <span
                          className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${getStatusColor(log.status)}`}
                        >
                          {log.status}
                        </span>
                      </div>
                      <div className="text-sm text-gray-500 mt-1">
                        {log.platform} • {log.product_count} produto{log.product_count !== 1 ? 's' : ''}
                      </div>
                      <div className="text-sm text-gray-700 mt-1">{log.message}</div>
                    </div>
                  </div>
                  
                  <div className="flex items-center space-x-4">
                    <div className="text-sm text-gray-500">
                      {formatDate(log.created_at)}
                    </div>
                    {log.details && (
                      <button
                        onClick={() => setSelectedLog(log)}
                        className="text-blue-600 hover:text-blue-800"
                        title="Ver detalhes"
                      >
                        <EyeIcon className="h-4 w-4" />
                      </button>
                    )}
                  </div>
                </div>
              </div>
            ))
          )}
        </div>
      </div>

      {/* Modal de detalhes do log */}
      {selectedLog && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg shadow-xl max-w-2xl w-full mx-4 max-h-96 overflow-y-auto">
            <div className="px-6 py-4 border-b border-gray-200">
              <h3 className="text-lg font-semibold text-gray-900">Detalhes do Log</h3>
            </div>
            <div className="p-6">
              <pre className="text-sm text-gray-800 whitespace-pre-wrap">
                {JSON.stringify(selectedLog.details, null, 2)}
              </pre>
            </div>
            <div className="px-6 py-4 border-t border-gray-200 flex justify-end">
              <button
                onClick={() => setSelectedLog(null)}
                className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 rounded-md hover:bg-gray-200"
              >
                Fechar
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default SyncDashboard;